/**
 * Flex layout solver — a bounded subset of CSS flexbox, in pure JS.
 *
 * Supports: row/column direction, gap, padding, grow, cross-axis align
 * (start/center/end/stretch), main-axis justify (start/center/end/between),
 * fixed sizes, and intrinsic sizing of text/glyph leaves. This is the
 * subset the `archui-*` layout primitives actually use — NOT full CSS.
 *
 * Two passes: `intrinsicSize` (content-size, bottom-up) feeds the main
 * solve (`layout`, top-down rect assignment).
 */
import {
  type Align,
  type Justify,
  type Node,
  type FontSpec,
  padOf,
} from "./model.js";
import { ellipsize, wrapText, wrapRuns, runFont, type Measure, type RichLine } from "./text.js";

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface LayoutBox {
  readonly rect: Rect;
  readonly node: Node;
  readonly children: readonly LayoutBox[];
  /** For single-line text nodes: the string to draw (post-ellipsis). */
  readonly displayText?: string;
  /** For wrapped text nodes: the lines to draw, top to bottom. */
  readonly lines?: readonly string[];
  /** For richtext nodes: wrapped lines of styled spans. */
  readonly richLines?: readonly RichLine[];
}

interface Size {
  readonly w: number;
  readonly h: number;
}

/** Content (intrinsic) size of a node, ignoring grow/stretch. */
export function intrinsicSize(node: Node, measure: Measure): Size {
  switch (node.type) {
    case "text": {
      const m = measure.text(node.text, node.style.font);
      // A WRAPPING text is width-flexible — report its min-content width
      // (longest word) so it doesn't force a flex row wider than the box; flex
      // then shrinks it to the available width and it wraps.
      //
      // An ELLIPSIZING text is different: CSS gives it a max-content basis and
      // truncates only under real pressure (`flex-shrink` + the backend's
      // `min-width: 0`). Reporting min-content here made the solver ellipsize
      // the text into its own longest word with the row wide open — a vendor
      // chip reading "Stri…" in the export while the live board showed
      // "Stripe Inc" in full. Full width is the basis; the shrink pass in
      // `layout` is what takes it away when the row is actually tight.
      if (node.style.wrap) {
        const words = node.text.split(/\s+/).filter(Boolean);
        const minW = words.reduce((mx, w) => Math.max(mx, measure.text(w, node.style.font).width), 0);
        return { w: minW, h: m.lineHeight };
      }
      return { w: m.width, h: m.lineHeight };
    }
    case "richtext": {
      const base = node.style.font;
      const mono = node.style.monoFamily;
      const lh = measure.text("M", base).lineHeight;
      const runW = (r: typeof node.runs[number]) =>
        r.glyph ? r.glyph.width : measure.text(r.text, runFont(base, r, mono)).width;
      // Flexible richtext (a wrapping name): min-content = widest unbreakable
      // token (an inline glyph or a single word), so flex shrinks + wraps it.
      // (Ellipsis-only richtext keeps the full-width basis for the same CSS
      // parity as plain text above; richtext always wraps in practice.)
      if (node.style.wrap || node.style.ellipsis) {
        let minW = 0;
        for (const r of node.runs) {
          if (r.glyph) minW = Math.max(minW, r.glyph.width);
          else for (const w of r.text.split(/\s+/).filter(Boolean)) minW = Math.max(minW, measure.text(w, runFont(base, r, mono)).width);
        }
        return { w: minW, h: lh };
      }
      return { w: node.runs.reduce((s, r) => s + runW(r), 0), h: lh };
    }
    case "glyph":
      return { w: node.width ?? node.viewW, h: node.height ?? node.viewH };
    case "image":
      return { w: node.width, h: node.height };
    case "spacer":
      return { w: 0, h: 0 };
    case "slot":
      return { w: node.width ?? 0, h: node.height ?? 0 };
    case "box": {
      if (node.width != null && node.height != null) {
        return { w: node.width, h: node.height };
      }
      const [pt, pr, pb, pl] = padOf(node.padding);
      const gap = node.gap ?? 0;
      const kids = node.children.map((c) => intrinsicSize(c, measure));
      const n = kids.length;
      const gaps = n > 1 ? gap * (n - 1) : 0;
      let w: number;
      let h: number;
      if (node.direction === "row") {
        w = kids.reduce((s, k) => s + k.w, 0) + gaps + pl + pr;
        h = kids.reduce((m, k) => Math.max(m, k.h), 0) + pt + pb;
      } else {
        h = kids.reduce((s, k) => s + k.h, 0) + gaps + pt + pb;
        w = kids.reduce((m, k) => Math.max(m, k.w), 0) + pl + pr;
      }
      return { w: node.width ?? w, h: node.height ?? h };
    }
  }
}

function mainOf(s: Size, dir: "row" | "column"): number {
  return dir === "row" ? s.w : s.h;
}

function crossStart(
  align: Align,
  childCross: number,
  contentCross: number,
): { offset: number; size: number } {
  if (align === "stretch") return { offset: 0, size: contentCross };
  const size = childCross;
  if (align === "center") return { offset: (contentCross - size) / 2, size };
  if (align === "end") return { offset: contentCross - size, size };
  return { offset: 0, size };
}

/**
 * Main-axis floor a node can shrink to — CSS's `min-content` under the
 * backend's own emissions: an ellipsizing text carries `min-width: 0`, so its
 * floor is 0; wrap text floors at its longest word; everything rigid floors at
 * its basis. A box recurses (sum along its main axis, max across), so a pill
 * around an ellipsis text is shrinkable while a pill around a glyph is not.
 */
function minContentMain(node: Node, dir: "row" | "column", measure: Measure): number {
  switch (node.type) {
    case "text":
      if (dir === "row" && node.style.ellipsis && !node.style.wrap) return 0;
      return mainOf(intrinsicSize(node, measure), dir);
    case "richtext":
      // Richtext wraps; its intrinsic is already the min-content.
      return mainOf(intrinsicSize(node, measure), dir);
    case "box": {
      if (node.growBand) return 0;
      const [pt, pr, pb, pl] = padOf(node.padding);
      const inner = node.direction === dir ? "sum" : "max";
      const kids = node.children.map((c) => minContentMain(c, dir, measure));
      const gaps = node.direction === dir && node.children.length > 1 ? (node.gap ?? 0) * (node.children.length - 1) : 0;
      const content = inner === "sum" ? kids.reduce((s, k) => s + k, 0) + gaps : kids.reduce((m, k) => Math.max(m, k), 0);
      const pad = dir === "row" ? pl + pr : pt + pb;
      const own = dir === "row" ? node.width : node.height;
      return own ?? content + pad;
    }
    default:
      return mainOf(intrinsicSize(node, measure), dir);
  }
}

/** Solve `node` into `frame`, producing a positioned rect tree. */
export function layout(node: Node, frame: Rect, measure: Measure): LayoutBox {
  if (node.type !== "box") {
    const lb: LayoutBox = { rect: frame, node, children: [] };
    if (node.type === "text") {
      const font = node.style.font;
      if (node.style.wrap) {
        const lh = measure.text(node.text, font).lineHeight;
        const cap = node.style.maxLines ?? Math.max(1, Math.floor(frame.h / lh));
        return { ...lb, lines: wrapText(node.text, frame.w, font, measure, cap) };
      }
      if (node.style.ellipsis) {
        return { ...lb, displayText: ellipsize(node.text, frame.w, font, measure) };
      }
      return { ...lb, displayText: node.text };
    }
    if (node.type === "richtext") {
      const font = node.style.font;
      const lh = measure.text("M", font).lineHeight;
      const cap = node.style.maxLines ?? Math.max(1, Math.floor(frame.h / lh));
      return { ...lb, richLines: wrapRuns(node.runs, frame.w, font, measure, cap, node.style.monoFamily) };
    }
    return lb;
  }

  const [pt, pr, pb, pl] = padOf(node.padding);
  const content: Rect = {
    x: frame.x + pl,
    y: frame.y + pt,
    w: Math.max(0, frame.w - pl - pr),
    h: Math.max(0, frame.h - pt - pb),
  };
  const dir = node.direction;
  const gap = node.gap ?? 0;
  const align: Align = node.align ?? "stretch";
  const justify: Justify = node.justify ?? "start";
  const mainTotal = dir === "row" ? content.w : content.h;
  const crossTotal = dir === "row" ? content.h : content.w;

  const kids = node.children;
  const intr = kids.map((c) => intrinsicSize(c, measure));
  // Height-for-width: in a column, a wrappable text/richtext child's main
  // (vertical) size is the wrapped line count at its assigned cross width —
  // not the 1-line intrinsic height. Without this, multi-line paragraphs clip.
  const bases = kids.map((c, i) => {
    // A band-gated box emits `flex: g×band 0 0` in the HTML backend — a ZERO
    // flex-basis. The solver bakes full detail (band = 1), so it must use the
    // same 0 basis, or the box would carry its content height as basis and
    // diverge from the live board (the live-vs-export slot geometry drift).
    // The grow distribution below then places it identically in both backends.
    if (c.type === "box" && c.growBand) return 0;
    if (dir === "column") {
      const cw = align === "stretch" ? crossTotal : intr[i]!.w;
      if (c.type === "text" && c.style.wrap) {
        const lh = measure.text(c.text, c.style.font).lineHeight;
        const cap = c.style.maxLines ?? Infinity;
        return wrapText(c.text, cw, c.style.font, measure, cap).length * lh;
      }
      if (c.type === "richtext") {
        const lh = measure.text("M", c.style.font).lineHeight;
        const cap = c.style.maxLines ?? Infinity;
        return wrapRuns(c.runs, cw, c.style.font, measure, cap, c.style.monoFamily).length * lh;
      }
    }
    return mainOf(intr[i]!, dir);
  });
  const n = kids.length;
  const gaps = n > 1 ? gap * (n - 1) : 0;
  const used = bases.reduce((s, b) => s + b, 0) + gaps;
  const free = mainTotal - used;
  // A band-gated box defaults to grow 1 (matching the HTML backend's
  // `flex: calc(grow ?? 1 …) 0 0`), so a `growBand` box with no explicit grow
  // still fills its cell in the solver instead of collapsing to its 0 basis.
  const growOf = (c: Node): number =>
    c.type === "box" && c.growBand ? (c.grow ?? 1) : (c.grow ?? 0);
  const totalGrow = kids.reduce((s, c) => s + growOf(c), 0);

  // Main-axis sizes after grow distribution. Growers also ABSORB overflow
  // (free < 0) — flex-shrink-like — so a too-wide flexible child (a long
  // name) is squeezed to the available width and wraps instead of spilling.
  const mainSizes = bases.map((b, i) => {
    const g = growOf(kids[i]!);
    if (totalGrow > 0 && g > 0 && free !== 0) return Math.max(0, b + (free * g) / totalGrow);
    if (totalGrow > 0 && free > 0) return b + (free * g) / totalGrow;
    return b;
  });

  // A tight row with NO growers still shrinks in CSS: every flex item has
  // `flex-shrink: 1`, floored at its min-content (`min-width: 0` for ellipsis
  // text — see minContentMain). Without this pass a content-hugging pill (a
  // chip) around an ellipsis text either had to lie about its basis (the old
  // min-content basis, which truncated the chip with the row wide open) or
  // spill past the row. Deficit distributes proportionally to each child's
  // slack (basis − min-content floor); CSS proper shrinks proportional to
  // BASIS with iterative floor-freezing, so a row mixing floor kinds (an
  // ellipsis text's 0 with a wrap text's longest-word) can converge a few px
  // differently — close enough for the chip rows this pass exists for, and
  // exact when the deficit fits uniformly or floors everyone. Rows only: in a column
  // the equivalent squeeze would squash wrapped paragraphs the backend's CSS
  // doesn't actually squash (bodies gate on bands instead).
  // The trigger needs an epsilon: padding is subtracted from the frame
  // non-associatively, so a row that fits EXACTLY computes free ≈ −1e-15, and
  // shrinking by that hands a text a width fractionally under its own measure —
  // `ellipsize` then collapses a perfectly fitting "PII" chip to "…". Overflow
  // below a hundredth of a pixel is not overflow.
  if (dir === "row" && free < -0.01 && totalGrow === 0) {
    const floors = kids.map((c) => minContentMain(c, "row", measure));
    const slack = bases.map((b, i) => Math.max(0, b - floors[i]!));
    const totalSlack = slack.reduce((s, v) => s + v, 0);
    if (totalSlack > 0) {
      const deficit = Math.min(-free, totalSlack);
      for (let i = 0; i < mainSizes.length; i++) {
        mainSizes[i] = bases[i]! - (deficit * slack[i]!) / totalSlack;
      }
    }
  }

  // Justify offset + inter-item spacing (only when grow didn't eat the free space).
  let cursor = 0;
  let between = gap;
  if (totalGrow === 0 && free > 0) {
    if (justify === "center") cursor = free / 2;
    else if (justify === "end") cursor = free;
    else if (justify === "between" && n > 1) between = gap + free / (n - 1);
  }

  const children: LayoutBox[] = [];
  for (let i = 0; i < n; i++) {
    const mainSize = mainSizes[i]!;
    const childCross = dir === "row" ? intr[i]!.h : intr[i]!.w;
    const { offset: crossOff, size: crossSize } = crossStart(align, childCross, crossTotal);

    const rect: Rect =
      dir === "row"
        ? { x: content.x + cursor, y: content.y + crossOff, w: mainSize, h: crossSize }
        : { x: content.x + crossOff, y: content.y + cursor, w: crossSize, h: mainSize };

    children.push(layout(kids[i]!, rect, measure));
    cursor += mainSize + between;
  }

  // Overlays: absolutely positioned within the box's OUTER rect, painted
  // after (on top of) the flow children. Document order = paint order, so
  // appending them last gives the right z-stacking in the SVG backend.
  for (const ov of node.overlays ?? []) {
    const s = intrinsicSize(ov.node, measure);
    const ow = ov.stretchX ? frame.w : s.w;
    const oh = ov.stretchY ? frame.h : s.h;
    const inset = ov.inset ?? 0;
    const left = frame.x + inset;
    const right = frame.x + frame.w - ow - inset;
    const top = frame.y + inset;
    const bottom = frame.y + frame.h - oh - inset;
    const cx = frame.x + (frame.w - ow) / 2;
    const cy = frame.y + (frame.h - oh) / 2;
    let ox: number;
    let oy: number;
    switch (ov.at) {
      case "top-left": ox = left; oy = top; break;
      case "top-right": ox = right; oy = top; break;
      case "bottom-left": ox = left; oy = bottom; break;
      case "bottom-right": ox = right; oy = bottom; break;
      case "top": ox = cx; oy = top; break;
      case "bottom": ox = cx; oy = bottom; break;
      case "left": ox = left; oy = cy; break;
      case "right": ox = right; oy = cy; break;
    }
    children.push(layout(ov.node, { x: ox, y: oy, w: ow, h: oh }, measure));
  }

  return { rect: frame, node, children };
}

/** Convenience: the font a text node carries (for callers/tests). */
export function textFont(node: Node): FontSpec | undefined {
  return node.type === "text" ? node.style.font : undefined;
}
